// Spec 078 FR-002/FR-028 — envio de modelo com validação client-side (extensão, tamanho, magic) e
// barra de progresso (XHR). A recusa diz o critério e o excedente, igual à do servidor.
import * as React from "react";
import { Upload } from "lucide-react";
import { MODEL3D_MAX_BYTES } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useReplaceModel3dFile, useUploadModel3d } from "../hooks";
import type { Model3dDetail, UploadProgress } from "@/types/models-3d.types";

const mb = (n: number) =>
  `${(n / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;

/** Espelho da validação do servidor (models-3d.validate.ts): recusa antes de subir o arquivo à toa. */
export async function precheckGlb(file: File): Promise<string | null> {
  if (!/\.glb$/i.test(file.name))
    return "Envie um arquivo .glb (glTF binário). Outros formatos não são aceitos.";
  if (file.size > MODEL3D_MAX_BYTES) {
    // Mesma frase do servidor (models-3d.validate.ts). Aqui o tamanho real é sempre conhecido.
    return `Arquivo de ${mb(file.size)} excede o limite de ${mb(MODEL3D_MAX_BYTES)} em ${mb(file.size - MODEL3D_MAX_BYTES)}. Otimize a malha e as texturas antes de enviar.`;
  }
  if (file.size < 12)
    return "O arquivo não é um glTF binário: o cabeçalho não começa com a assinatura glTF.";
  const head = new DataView(await file.slice(0, 12).arrayBuffer());
  if (head.getUint32(0, true) !== 0x46546c67)
    return "O arquivo não é um glTF binário: o cabeçalho não começa com a assinatura glTF.";
  if (head.getUint32(4, true) !== 2)
    return "O arquivo é glTF de uma versão não suportada. Exporte como glTF 2.0 binário.";
  if (head.getUint32(8, true) !== file.size)
    return "O arquivo está truncado ou corrompido: o tamanho declarado no cabeçalho não bate com o recebido.";
  return null;
}

export interface UploadDialogProps {
  /** presente ⇒ substitui o arquivo deste modelo (mantém calibração) */
  replaceOf?: { id: string; name: string } | null;
  onClose: () => void;
  onDone: (model: Model3dDetail) => void;
}

export function UploadDialog({
  replaceOf = null,
  onClose,
  onDone,
}: UploadDialogProps): React.ReactElement {
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState(replaceOf?.name ?? "");
  const [error, setError] = React.useState<string | null>(null);
  const [progress, setProgress] = React.useState<UploadProgress | null>(null);
  const create = useUploadModel3d();
  const replace = useReplaceModel3dFile(replaceOf?.id ?? "");
  const pending = create.isPending || replace.isPending;

  async function pick(f: File | null) {
    setFile(f);
    setError(null);
    if (!f) return;
    setError(await precheckGlb(f));
    if (!replaceOf && !name.trim()) setName(f.name.replace(/\.glb$/i, ""));
  }

  async function submit() {
    if (!file || error) return;
    try {
      const args = { file, name: replaceOf ? undefined : name.trim(), onProgress: setProgress };
      const model = replaceOf ? await replace.mutateAsync(args) : await create.mutateAsync(args);
      toast.success(
        replaceOf
          ? "Arquivo substituído. Revise os pontos de entrada e saída."
          : `Modelo "${model.name}" enviado.`
      );
      onDone(model);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, "Não foi possível enviar o arquivo. Tente novamente."));
      setProgress(null);
    }
  }

  return (
    <ModalOverlay
      onClose={onClose}
      title={replaceOf ? "Substituir arquivo do modelo" : "Enviar modelo 3D"}
      className="max-w-lg"
      confirmDismiss={Boolean(file) && !pending}
    >
      <div className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {replaceOf ? `Substituir arquivo de "${replaceOf.name}"` : "Enviar modelo 3D"}
          </h2>
          <p className="text-sm text-text-secondary">
            Arquivo <span className="font-mono">.glb</span> (glTF 2.0 binário) de até{" "}
            {mb(MODEL3D_MAX_BYTES)}. Otimize antes: textura ≤ 1024², malha comprimida (meshopt).
          </p>
        </div>
        {!replaceOf ? (
          <div className="space-y-1.5">
            <Label htmlFor="m3d-name">Nome</Label>
            <Input
              id="m3d-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="Ex.: Debulhadora"
            />
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="m3d-file">Arquivo</Label>
          <Input
            id="m3d-file"
            type="file"
            accept=".glb,model/gltf-binary"
            onChange={(e) => void pick(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <p className="text-xs text-text-tertiary">
              {file.name} · {mb(file.size)}
            </p>
          ) : null}
        </div>
        {error ? <Notify.Banner variant="danger" title={error} /> : null}
        {progress ? (
          <div className="space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-band">
              <div
                className="h-full w-[var(--up)] rounded-full bg-primary transition-[width]"
                style={{ "--up": `${Math.round(progress.pct)}%` } as React.CSSProperties}
              />
            </div>
            <p className="text-xs text-text-tertiary">Enviando… {Math.round(progress.pct)}%</p>
          </div>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!file || Boolean(error) || pending || (!replaceOf && !name.trim())}
          >
            <Upload className="h-4 w-4" aria-hidden />
            {pending ? "Enviando…" : replaceOf ? "Substituir" : "Enviar"}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
